import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader,
  CardBody, 
  Button, 
  OutlineButton, 
  Input,
  Select,
  Textarea,
  LoadingSpinner
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { EmployeeLoan } from '../../types';
import apiService from '../../services/api';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    color: var(--primary-color);
    margin-bottom: 1rem;
    border-bottom: 2px solid var(--light-gray);
    padding-bottom: 0.5rem;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
  }
  
  .error {
    color: var(--danger-color);
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SummaryCard = styled.div`
  background: var(--light-gray);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-top: 1rem;
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--primary-color);
  }
  
  .summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    
    .label {
      font-weight: 500;
    }
    
    .value {
      color: var(--dark-gray);
    }
  }
`;

const LoanForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  
  const [formData, setFormData] = useState({
    employeeId: '',
    purpose: '',
    principal: '',
    interestRate: '',
    duration: '',
    startDate: '',
    notes: ''
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loanSummary, setLoanSummary] = useState<any>(null);

  useEffect(() => {
    loadEmployees();
    if (id) {
      loadLoan();
    }
  }, [id]);

  const loadEmployees = async () => {
    try {
      const response = await apiService.getUsers();
      if (response.success && response.data) {
        // Filter to show only active users
        const staffMembers = response.data.filter((user: any) => 
          user.status === 'Active'
        );
        setEmployees(staffMembers);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadLoan = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmployeeLoan(id!);
      if (response.success && response.data) {
        const loan = response.data;
        setFormData({
          employeeId: loan.employeeId,
          purpose: loan.purpose,
          principal: loan.principal.toString(),
          interestRate: loan.interestRate.toString(),
          duration: loan.duration.toString(),
          startDate: new Date(loan.startDate).toISOString().split('T')[0],
          notes: loan.notes || ''
        });
        calculateLoanSummary();
      }
    } catch (error) {
      console.error('Error loading loan:', error);
      setError('Failed to load loan details');
    } finally {
      setLoading(false);
    }
  };

  const calculateLoanSummary = () => {
    const principal = parseFloat(formData.principal) || 0;
    const interestRate = parseFloat(formData.interestRate) || 0;
    const duration = parseInt(formData.duration) || 0;

    if (principal > 0 && interestRate > 0 && duration > 0) {
      const monthlyInterest = interestRate / 100 / 12;
      const totalInterest = principal * monthlyInterest * duration;
      const totalAmount = principal + totalInterest;
      const installmentAmount = totalAmount / duration;

      setLoanSummary({
        principal,
        interestRate,
        duration,
        totalInterest,
        totalAmount,
        installmentAmount
      });
    } else {
      setLoanSummary(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Recalculate summary for relevant fields
    if (['principal', 'interestRate', 'duration'].includes(name)) {
      setTimeout(calculateLoanSummary, 100);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.employeeId) errors.employeeId = 'Employee is required';
    if (!formData.purpose) errors.purpose = 'Purpose is required';
    if (!formData.principal || parseFloat(formData.principal) <= 0) {
      errors.principal = 'Principal amount must be greater than 0';
    }
    if (!formData.interestRate || parseFloat(formData.interestRate) <= 0) {
      errors.interestRate = 'Interest rate must be greater than 0';
    }
    if (!formData.duration || parseInt(formData.duration) <= 0) {
      errors.duration = 'Duration must be greater than 0';
    }
    if (!formData.startDate) errors.startDate = 'Start date is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const loanData = {
        employeeId: formData.employeeId,
        purpose: formData.purpose,
        principal: parseFloat(formData.principal),
        interestRate: parseFloat(formData.interestRate),
        duration: parseInt(formData.duration),
        startDate: new Date(formData.startDate),
        notes: formData.notes
      };

      let response;
      if (id) {
        response = await apiService.updateEmployeeLoan(id, loanData);
      } else {
        response = await apiService.createEmployeeLoan(loanData);
      }

      if (response.success) {
        navigate('/office/loans');
      } else {
        setError(response.message || 'Failed to save loan');
      }
    } catch (error: any) {
      console.error('Error saving loan:', error);
      setError(error.message || 'Failed to save loan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <FormContainer>
      <Card>
        <CardHeader>
          <h1 className="page-title">
            {id ? 'Edit Employee Loan' : 'New Employee Loan'}
          </h1>
        </CardHeader>
        <CardBody>
          {error && (
            <div style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <FormSection>
              <h3>Loan Details</h3>
              
              <FormRow>
                <FormGroup>
                  <label htmlFor="employeeId">Employee *</label>
                  <Select
                    id="employeeId"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    disabled={!!id}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(employee => (
                      <option key={employee._id} value={employee._id}>
                        {employee.name} - {employee.role}
                      </option>
                    ))}
                  </Select>
                  {formErrors.employeeId && <div className="error">{formErrors.employeeId}</div>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="purpose">Purpose *</label>
                  <Input
                    id="purpose"
                    name="purpose"
                    type="text"
                    value={formData.purpose}
                    onChange={handleInputChange}
                    placeholder="e.g., Emergency expenses, Education, etc."
                  />
                  {formErrors.purpose && <div className="error">{formErrors.purpose}</div>}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="principal">Principal Amount (TZS) *</label>
                  <Input
                    id="principal"
                    name="principal"
                    type="number"
                    value={formData.principal}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    step="1000"
                  />
                  {formErrors.principal && <div className="error">{formErrors.principal}</div>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="interestRate">Interest Rate (%) *</label>
                  <Input
                    id="interestRate"
                    name="interestRate"
                    type="number"
                    value={formData.interestRate}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  {formErrors.interestRate && <div className="error">{formErrors.interestRate}</div>}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="duration">Duration (Months) *</label>
                  <Input
                    id="duration"
                    name="duration"
                    type="number"
                    value={formData.duration}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="1"
                    max="120"
                  />
                  {formErrors.duration && <div className="error">{formErrors.duration}</div>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="startDate">Start Date *</label>
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={handleInputChange}
                  />
                  {formErrors.startDate && <div className="error">{formErrors.startDate}</div>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <label htmlFor="notes">Notes</label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes about the loan..."
                  rows={3}
                />
              </FormGroup>
            </FormSection>

            {loanSummary && (
              <FormSection>
                <h3>Loan Summary</h3>
                <SummaryCard>
                  <div className="summary-row">
                    <span className="label">Principal Amount:</span>
                    <span className="value">{loanSummary.principal.toLocaleString()} TZS</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Interest Rate:</span>
                    <span className="value">{loanSummary.interestRate}% per annum</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Duration:</span>
                    <span className="value">{loanSummary.duration} months</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Total Interest:</span>
                    <span className="value">{loanSummary.totalInterest.toLocaleString()} TZS</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Total Amount:</span>
                    <span className="value">{loanSummary.totalAmount.toLocaleString()} TZS</span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Monthly Installment:</span>
                    <span className="value">{loanSummary.installmentAmount.toLocaleString()} TZS</span>
                  </div>
                </SummaryCard>
              </FormSection>
            )}

            <ButtonGroup>
              <OutlineButton type="button" onClick={() => navigate('/office/loans')}>
                Cancel
              </OutlineButton>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : (id ? 'Update Loan' : 'Create Loan')}
              </Button>
            </ButtonGroup>
          </form>
        </CardBody>
      </Card>
    </FormContainer>
  );
};

export default LoanForm;
